import type { LayoutInput, LayoutResult, FlowOptions, LayoutNode, Line } from '../types';
import { FLOW_DEFAULTS } from '../defaults';
import { shapeBounds } from '../utils/geometry';

/**
 * Dagre-style directed flow layout.
 *
 * Assigns nodes to ranks (levels), orders within ranks to minimize crossings,
 * then positions with spacing.
 */
export function flow(
	input: LayoutInput,
	options?: Partial<FlowOptions>,
): LayoutResult {
	const opts: FlowOptions = { ...FLOW_DEFAULTS, ...options };
	const { nodes, edges } = input;

	if (nodes.length === 0) return { nodes: [] };

	const nodeMap = new Map(nodes.map((n) => [n.id, n]));
	const isHorizontal = opts.direction === 'right' || opts.direction === 'left';
	const isReversed = opts.direction === 'up' || opts.direction === 'left';

	// === Step 1: Rank assignment (longest-path from sources) ===
	const adj = new Map<string, string[]>();
	const inDegree = new Map<string, number>();
	for (const n of nodes) {
		adj.set(n.id, []);
		inDegree.set(n.id, 0);
	}
	for (const e of edges) {
		if (!nodeMap.has(e.source) || !nodeMap.has(e.target)) continue;
		adj.get(e.source)!.push(e.target);
		inDegree.set(e.target, (inDegree.get(e.target) ?? 0) + 1);
	}

	// Topological sort with rank assignment
	const rank = new Map<string, number>();
	const queue: string[] = [];

	for (const n of nodes) {
		if ((inDegree.get(n.id) ?? 0) === 0) {
			queue.push(n.id);
			rank.set(n.id, 0);
		}
	}

	// Handle cycles: if no sources found, start with first node
	if (queue.length === 0 && nodes.length > 0) {
		queue.push(nodes[0].id);
		rank.set(nodes[0].id, 0);
	}

	const processed = new Set<string>();
	while (queue.length > 0) {
		const current = queue.shift()!;
		if (processed.has(current)) continue;
		processed.add(current);
		const currentRank = rank.get(current)!;

		for (const target of adj.get(current) ?? []) {
			const newRank = currentRank + 1;
			if (!rank.has(target) || newRank > rank.get(target)!) {
				rank.set(target, newRank);
			}
			// Decrement in-degree; add to queue when all parents processed
			const newInDegree = (inDegree.get(target) ?? 1) - 1;
			inDegree.set(target, newInDegree);
			if (newInDegree <= 0) {
				queue.push(target);
			}
		}
	}

	// Assign unranked nodes (isolated/cycle members) to rank 0
	for (const n of nodes) {
		if (!rank.has(n.id)) rank.set(n.id, 0);
	}

	// === Step 2: Group by rank ===
	const maxRank = Math.max(...[...rank.values()], 0);
	const ranks: string[][] = Array.from({ length: maxRank + 1 }, () => []);
	for (const n of nodes) {
		ranks[rank.get(n.id)!].push(n.id);
	}

	// === Step 3: Minimize crossings (simple barycenter heuristic) ===
	if (opts.minimizeCrossings) {
		for (let pass = 0; pass < 4; pass++) {
			for (let r = 1; r <= maxRank; r++) {
				const positions = new Map<string, number>();
				// Compute barycenter of each node based on parent positions
				for (const nodeId of ranks[r]) {
					const parents: number[] = [];
					for (const e of edges) {
						if (e.target === nodeId && rank.get(e.source) === r - 1) {
							const parentIdx = ranks[r - 1].indexOf(e.source);
							if (parentIdx >= 0) parents.push(parentIdx);
						}
					}
					if (parents.length > 0) {
						positions.set(
							nodeId,
							parents.reduce((a, b) => a + b, 0) / parents.length,
						);
					} else {
						positions.set(nodeId, ranks[r].indexOf(nodeId));
					}
				}
				ranks[r].sort(
					(a, b) => (positions.get(a) ?? 0) - (positions.get(b) ?? 0),
				);
			}
		}
	}

	// === Step 4: Compute positions ===
	const nodePositions = new Map<string, { x: number; y: number }>();

	for (let r = 0; r <= maxRank; r++) {
		const rankNodes = ranks[r];
		if (rankNodes.length === 0) continue;

		// Get node sizes in this rank
		const sizes = rankNodes.map((id) => shapeBounds(nodeMap.get(id)!.shape));
		const maxNodeRankSize = Math.max(
			...sizes.map((b) => (isHorizontal ? b.width : b.height)),
		);

		// Rank position along primary axis
		let rankPos = r * (maxNodeRankSize + opts.spacing.rank);
		if (isReversed) rankPos = -rankPos;

		// Cross-axis positioning
		const totalCross = sizes.reduce(
			(sum, b) => sum + (isHorizontal ? b.height : b.width),
			0,
		) + (rankNodes.length - 1) * opts.spacing.node;

		let cursor = -totalCross / 2; // center the rank

		for (let i = 0; i < rankNodes.length; i++) {
			const b = sizes[i];
			const nodeSize = isHorizontal ? b.height : b.width;

			let crossPos = cursor;
			if (opts.align === 'center') {
				crossPos = cursor; // already centered
			} else if (opts.align === 'end') {
				crossPos = cursor + (nodeSize); // aligned to end
			}

			if (isHorizontal) {
				nodePositions.set(rankNodes[i], { x: rankPos, y: crossPos });
			} else {
				nodePositions.set(rankNodes[i], { x: crossPos, y: rankPos });
			}

			cursor += nodeSize + opts.spacing.node;
		}
	}

	// Offset to preserve first node's original position
	const firstNode = nodes[0];
	const firstBounds = shapeBounds(firstNode.shape);
	const firstPos = nodePositions.get(firstNode.id);
	const offsetX = firstPos ? firstBounds.x - firstPos.x : 0;
	const offsetY = firstPos ? firstBounds.y - firstPos.y : 0;

	// Build result with edges
	const resultEdges: { source: string; target: string; line: Line }[] = [];
	if (opts.edgeRouting === 'straight') {
		for (const e of edges) {
			const sPos = nodePositions.get(e.source);
			const tPos = nodePositions.get(e.target);
			if (sPos && tPos) {
				const sb = shapeBounds(nodeMap.get(e.source)!.shape);
				const tb = shapeBounds(nodeMap.get(e.target)!.shape);
				resultEdges.push({
					source: e.source,
					target: e.target,
					line: {
						x1: sPos.x + offsetX + sb.width / 2,
						y1: sPos.y + offsetY + sb.height / 2,
						x2: tPos.x + offsetX + tb.width / 2,
						y2: tPos.y + offsetY + tb.height / 2,
					},
				});
			}
		}
	}

	return {
		nodes: nodes.map((n) => {
			const pos = nodePositions.get(n.id);
			if (!pos) {
				const b = shapeBounds(n.shape);
				return { id: n.id, x: b.x, y: b.y };
			}
			return {
				id: n.id,
				x: pos.x + offsetX,
				y: pos.y + offsetY,
			};
		}),
		edges: resultEdges.length > 0 ? resultEdges : undefined,
	};
}
